import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, 
  X, 
  Volume2, 
  VolumeX, 
  Share2, 
  CheckCircle2, 
  XCircle,
  MapPin,
  Link2,
  Facebook,
  Twitter,
  MessageCircle,
  Mail,
  Copy,
  Check,
  MessageSquare,
  Heart,
  Bookmark,
  Clock,
  Flag,
  MoreVertical,
  ChevronLeft,
  AlertTriangle,
  Send,
  RefreshCw
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback } from './ui/avatar';
import { ScrollArea } from './ui/scroll-area';
import type { Incident } from '../types';
import { updateIncidentPresence } from '../services/incidents';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { toast } from './ui/use-toast';
import { useMapPosition } from '../contexts/MapPositionContext';
import { fetchIncidentBySlug } from '../services/incidents';
import SEOMeta from './SEOMeta';
import Header from './Header';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { EventCategories, EventActions } from '../lib/analytics';
import { trackEvent, trackException } from '../lib/analytics';
import { Helmet } from 'react-helmet-async';
import { getIncidentIcon } from '../utils/incidentIcons';
import { 
  fetchComments, 
  addComment, 
  likeComment, 
  subscribeToComments 
} from '../services/comments';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';
import { useAtom } from 'jotai';
import { userAtom } from '../atoms/auth';

interface IncidentPageProps {
  incident?: Incident;
  onBack: () => void;
}

export default function IncidentPage({ incident, onBack }: IncidentPageProps) {
  // 1. FIRST: Declare ALL state variables at the top
  const navigate = useNavigate();
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { lastPosition } = useMapPosition();
  const { slug } = useParams();
  const videoRef = useRef<HTMLVideoElement>(null);
  
  // All useState hooks must be here at the top
  const [isUpdating, setIsUpdating] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [hasCopied, setHasCopied] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [liked, setLiked] = useState(false);
  const [saved, setSaved] = useState(false);
  const [currentMediaIndex, setCurrentMediaIndex] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  
  // Comment-related state
  const [comments, setComments] = useState<Comment[]>([]);
  const [commentInput, setCommentInput] = useState('');
  const [isSubmittingComment, setIsSubmittingComment] = useState(false);
  
  // Replace the useState and useEffect for user with useAtom
  const [user] = useAtom(userAtom);
  
  // 2. SECOND: All queries and data fetching
  const { data: fetchedIncident, isLoading } = useQuery({
    queryKey: ['incident', slug],
    queryFn: () => fetchIncidentBySlug(slug!),
    enabled: !incident && !!slug,
    staleTime: 1000 * 60 * 5 // 5 minutes
  });

  const currentIncident = incident || fetchedIncident;

  // 3. THIRD: All useEffect hooks
  // Handle video events
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);
    const handleEnded = () => setIsPlaying(false);

    video.addEventListener('play', handlePlay);
    video.addEventListener('pause', handlePause);
    video.addEventListener('ended', handleEnded);

    return () => {
      video.removeEventListener('play', handlePlay);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('ended', handleEnded);
    };
  }, []);

  // Fetch comments
  useEffect(() => {
    let isMounted = true;
    
    const loadComments = async () => {
      if (!currentIncident?.id) return;
      
      try {
        const fetchedComments = await fetchComments(currentIncident.id);
        if (isMounted) {
          setComments(fetchedComments || []);
        }
      } catch (error) {
        console.error('Failed to fetch comments:', error);
      }
    };
    
    loadComments();
    
    return () => {
      isMounted = false;
    };
  }, [currentIncident?.id]);
  
  // Set up real-time subscription
  useEffect(() => {
    let subscription: any = null;
    
    if (currentIncident?.id) {
      try {
        subscription = subscribeToComments(currentIncident.id, (newComment) => {
          setComments(prevComments => [...prevComments, newComment]);
        });
      } catch (error) {
        console.error('Failed to subscribe to comments:', error);
      }
    }
    
    return () => {
      if (subscription) {
        try {
          subscription.unsubscribe();
        } catch (error) {
          console.error('Failed to unsubscribe from comments:', error);
        }
      }
    };
  }, [currentIncident?.id]);

  // 4. FOURTH: All handler functions
  const handleBack = () => {
    navigate('/', {
      state: {
        returnFromIncident: true,
        mapPosition: lastPosition
      }
    });
  };

  const handlePresenceUpdate = async (type: 'still-here' | 'no-longer-here') => {
    if (isUpdating || !currentIncident) return;

    try {
      setIsUpdating(true);
      await updateIncidentPresence(currentIncident.id, type);
      
      trackEvent(
        EventCategories.Incident,
        EventActions.UpdatePresence,
        type,
        currentIncident.id
      );

      await queryClient.invalidateQueries({ queryKey: ['incidents'] });
      
      toast({
        title: type === 'still-here' ? 'Confirmed incident is still active' : 'Reported incident is no longer active',
        description: 'Thank you for helping keep the community informed',
      });
      
    } catch (error) {
      trackException(
        `Failed to update presence: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
      
      toast({
        title: 'Failed to update incident status',
        description: 'Please try again later',
        variant: 'destructive',
      });
    } finally {
      setIsUpdating(false);
    }
  };

  // Media navigation
  const nextMedia = () => {
    if (currentIncident?.incident_media && currentMediaIndex < currentIncident.incident_media.length - 1) {
      setCurrentMediaIndex(currentMediaIndex + 1);
      setIsPlaying(false);
    }
  };

  const prevMedia = () => {
    if (currentMediaIndex > 0) {
      setCurrentMediaIndex(currentMediaIndex - 1);
      setIsPlaying(false);
    }
  };

  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
  };

  const handleMediaTap = () => {
    if (currentIncident?.incident_media && 
        currentIncident.incident_media[currentMediaIndex] && 
        currentIncident.incident_media[currentMediaIndex].type === 'video') {
      togglePlay();
    }
  };

  // Comment handlers
  const handleSubmitComment = async () => {
    if (!commentInput.trim() || !currentIncident?.id || isSubmittingComment) return;
    
    if (!user) {
      toast({
        title: "Authentication Required",
        description: "Please sign in to comment on incidents",
        variant: "destructive"
      });
      return;
    }
    
    try {
      setIsSubmittingComment(true);
      
      const newComment = await addComment(currentIncident.id, commentInput.trim(), user);
      
      if (newComment) {
        setCommentInput('');
        
        trackEvent(
          EventCategories.Incident,
          EventActions.Comment,
          'add-comment',
          currentIncident.id
        );
      }
    } catch (error) {
      toast({
        title: "Failed to add comment",
        description: "Please try again later",
        variant: "destructive"
      });
    } finally {
      setIsSubmittingComment(false);
    }
  };
  
  const handleLikeComment = async (commentId: string) => {
    if (!user) {
      toast({
        title: "Authentication Required",
        description: "Please sign in to like comments",
        variant: "destructive"
      });
      return;
    }
    
    try {
      const success = await likeComment(commentId);
      
      if (success) {
        // Update the UI optimistically
        setComments(prevComments => 
          prevComments.map(comment => 
            comment.id === commentId 
              ? { ...comment, likes: comment.likes + 1 } 
              : comment
          )
        );
      }
    } catch (error) {
      toast({
        title: "Failed to like comment",
        description: "Please try again later",
        variant: "destructive"
      });
    }
  };

  // Handle native share functionality
  const handleNativeShare = async () => {
    try {
      await navigator.share(shareData);
      toast({
        title: t('incident.share.success'),
        description: t('incident.share.shared'),
      });
      
      trackEvent(
        EventCategories.Incident,
        EventActions.Share,
        'native-share',
      );
    } catch (error) {
      if (error instanceof Error && error.name !== 'AbortError') {
        toast({
          title: t('incident.share.error'),
          description: t('incident.share.tryAgain'),
          variant: 'destructive',
        });
      }
    }
  };

  // Handle copying link to clipboard
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareData.url);
      setHasCopied(true);
      toast({
        title: t('incident.share.success'),
        description: t('incident.share.copied'),
      });
      setTimeout(() => setHasCopied(false), 2000);
      
      trackEvent(
        EventCategories.Incident,
        EventActions.Share,
        'copy-link',
      );
    } catch (error) {
      toast({
        title: t('incident.share.error'),
        description: t('incident.share.copyFailed'),
        variant: 'destructive',
      });
    }
  };

  // Handle social media sharing
  const handleSocialShare = (platform: string) => {
    const encodedUrl = encodeURIComponent(shareData.url);
    const encodedText = encodeURIComponent(shareData.text);
    const encodedTitle = encodeURIComponent(shareData.title);

    const shareUrls: {[key: string]: string} = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`,
      whatsapp: `https://wa.me/?text=${encodedText}%20${encodedUrl}`,
      email: `mailto:?subject=${encodedTitle}&body=${encodedText}%0A%0A${encodedUrl}`,
      sms: `sms:?body=${encodedText} ${encodedUrl}`,
    };

    trackEvent(
      EventCategories.Incident,
      EventActions.Share,
      platform,
    );

    const url = shareUrls[platform];
    if (!url) return;

    // Open in a new window with specific dimensions
    const width = 550;
    const height = 400;
    const left = (window.innerWidth - width) / 2;
    const top = (window.innerHeight - height) / 2;

    if (platform === 'email' || platform === 'sms') {
      window.location.href = url;
    } else {
      window.open(
        url,
        'share',
        `width=${width},height=${height},left=${left},top=${top},popup=yes`
      );
    }
  };

  // Handle like action
  const handleLike = () => {
    setLiked(!liked);
    if (!liked) {
      handlePresenceUpdate('still-here');
    }
  };

  // Handle save action
  const handleSave = () => {
    setSaved(!saved);
    if (!saved) {
      handlePresenceUpdate('no-longer-here');
    }
  };

  // 5. FIFTH: Early return for loading state
  if (isLoading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-black">
        <div className="animate-pulse flex flex-col items-center">
          <div className="w-12 h-12 rounded-full bg-pink-500/30 mb-4"></div>
          <div className="h-2 w-24 bg-gray-200/20 rounded"></div>
        </div>
      </div>
    );
  }

  if (!currentIncident) {
    return (
      <div className="fixed inset-0 bg-black flex flex-col items-center justify-center text-white">
        <AlertTriangle size={48} className="mb-4 text-pink-500" />
        <h1 className="text-xl font-semibold mb-2">{t('incident.notFound')}</h1>
        <Button 
          onClick={onBack}
          className="mt-4 bg-pink-500 hover:bg-pink-600 text-white border-none"
        >
          <ArrowLeft className="h-5 w-5 mr-2" />
          {t('nav.back')}
        </Button>
      </div>
    );
  }

  // 6. SIXTH: Prepare data for rendering
  const shareData = {
    title: t(`incident.types.${currentIncident.type}`),
    text: currentIncident.description || t(`incident.types.${currentIncident.type}`),
    url: `${window.location.origin}/incident/${currentIncident.slug}`,
  };

  // Get media files from incident
  const mediaFiles = currentIncident.incident_media?.map(media => ({
    type: media.type,
    url: media.url,
    timestamp: media.created_at || currentIncident.created_at
  })) || [];

  // Calculate reaction counts
  const reactions = {
    likes: liked ? (currentIncident.still_here_count + 1) : currentIncident.still_here_count,
    comments: comments.length,
    shares: 0,
    saves: saved ? (currentIncident.no_longer_here_count + 1) : currentIncident.no_longer_here_count,
  };

  // Get severity color
  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'CRITICAL': return '#FF375F';
      case 'HIGH': return '#FF7A00';
      case 'MODERATE': return '#FFBD00';
      case 'LOW': return '#32D74B';
      default: return '#FF7A00';
    }
  };

  // Get incident icon
  const getIncidentIcon = (type: string) => {
    switch (type) {
      case 'GANG_ACTIVITY': return '👥';
      case 'SEXUAL_VIOLENCE': return '⚠️';
      case 'CIVIL_UNREST': return '🔥';
      case 'KIDNAPPING': return '🚨';
      case 'ROBBERY': return '💰';
      case 'NATURAL_DISASTER': return '🌪️';
      case 'ROAD_CLOSURE': return '🚧';
      default: return '⚠️';
    }
  };

  // Format timestamp to relative time (e.g., "2h ago")
  const getRelativeTime = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 60) return `${diffMins}m ago`;
    
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  // Get updates from the incident or use an empty array if none exist
  const updates = currentIncident.updates || [];

  // Determine if we show updates section
  const hasUpdates = updates.length > 0;

  // 7. FINALLY: Return the JSX
  return (
    <>
      <Helmet>
        {/* Dynamic meta tags based on incident data */}
        {currentIncident && (
          <>
            <title>{`${currentIncident.type} Incident - RapoKrim`}</title>
            <meta name="description" content={shareData.text} />
            
            {/* Open Graph / Facebook */}
            <meta property="og:type" content="article" />
            <meta property="og:url" content={`https://rapokrim.com/incident/${currentIncident.slug}`} />
            <meta property="og:title" content={`${currentIncident.type} Incident - RapoKrim`} />
            <meta property="og:description" content={shareData.text} />
            <meta property="og:image" content={mediaFiles.length > 0 ? mediaFiles[0].url : getShareImage()} />
            <meta property="og:image:alt" content={`${currentIncident.type} incident in ${currentIncident.location_zone}`} />
            
            {/* Twitter */}
            <meta property="twitter:card" content="summary_large_image" />
            <meta property="twitter:url" content={`https://rapokrim.com/incident/${currentIncident.slug}`} />
            <meta property="twitter:title" content={`${currentIncident.type} Incident - RapoKrim`} />
            <meta property="twitter:description" content={shareData.text} />
            <meta property="twitter:image" content={mediaFiles.length > 0 ? mediaFiles[0].url : getShareImage()} />
          </>
        )}
      </Helmet>
      
      {/* Full-screen immersive TikTok-style view */}
      <div className="fixed inset-0 bg-black flex flex-col z-50">
        {/* Header */}
        <div className="absolute top-0 left-0 right-0 z-50 p-4 flex items-center justify-between text-white">
          <button 
            onClick={handleBack} 
            className="p-2 bg-black bg-opacity-40 rounded-full hover:bg-opacity-60 transition-colors"
            aria-label="Go back"
          >
            <ChevronLeft size={28} />
          </button>
          <div className="flex items-center space-x-3">
            <button 
              className="p-1"
              onClick={() => {
                toast({
                  title: "Incident Reported",
                  description: "Thank you for flagging this content for review.",
                });
              }}
            >
              <Flag size={22} />
            </button>
            <button 
              className="p-1"
              onClick={handleNativeShare}
            >
              <Share2 size={22} />
            </button>
            <button className="p-1">
              <MoreVertical size={22} />
            </button>
          </div>
        </div>

        {/* Media Gallery */}
        <div className="relative flex-1 bg-black" onClick={handleMediaTap}>
          {mediaFiles.length > 0 ? (
            <>
              {currentMediaIndex > 0 && (
                <div className="absolute top-1/2 left-2 z-10 transform -translate-y-1/2 bg-black bg-opacity-50 rounded-full p-1" onClick={(e) => { e.stopPropagation(); prevMedia(); }}>
                  <ChevronLeft size={24} className="text-white" />
                </div>
              )}
              
              {mediaFiles.map((media, index) => (
                <div 
                  key={index} 
                  className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${index === currentMediaIndex ? 'opacity-100 z-1' : 'opacity-0 -z-10'}`}
                >
                  {media.type === 'video' ? (
                    <video 
                      ref={index === currentMediaIndex ? videoRef : null}
                      src={media.url} 
                      className="h-full w-full object-contain" 
                      playsInline
                      loop
                      muted={isMuted}
                    />
                  ) : (
                    <img 
                      src={media.url} 
                      alt={`Evidence ${index + 1}`}
                      className="h-full w-full object-contain"
                    />
                  )}
                </div>
              ))}
              
              {currentMediaIndex < mediaFiles.length - 1 && (
                <div className="absolute top-1/2 right-2 z-10 transform -translate-y-1/2 bg-black bg-opacity-50 rounded-full p-1" onClick={(e) => { e.stopPropagation(); nextMedia(); }}>
                  <ChevronLeft size={24} className="text-white transform rotate-180" />
                </div>
              )}

              {/* Media indicators */}
              {mediaFiles.length > 1 && (
                <div className="absolute top-4 left-0 right-0 flex justify-center z-10">
                  <div className="flex space-x-1">
                    {mediaFiles.map((_, index) => (
                      <div 
                        key={index} 
                        className={`h-1 w-4 rounded-full ${index === currentMediaIndex ? 'bg-white' : 'bg-white bg-opacity-40'}`}
                      ></div>
                    ))}
                  </div>
                </div>
              )}

              {/* Media type indicator */}
              {mediaFiles[currentMediaIndex]?.type === 'video' && (
                <div className="absolute top-12 right-4 z-10 bg-black bg-opacity-50 px-2 py-1 rounded text-xs text-white">
                  {isPlaying ? 'Playing' : 'Tap to play'}
                </div>
              )}
            </>
          ) : (
            // Fallback for no media - show a color background based on incident type
            <div className="absolute inset-0 bg-gradient-to-b from-gray-800 to-black">
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-8xl">{getIncidentIcon(currentIncident.type)}</span>
              </div>
            </div>
          )}

          {/* Incident type badge */}
          <div className="absolute top-4 left-4 z-10 flex items-center space-x-2">
            <div className="bg-black bg-opacity-50 px-3 py-1 rounded-full flex items-center">
              <span className="mr-1">{getIncidentIcon(currentIncident.type)}</span>
              <span className="text-white font-medium text-sm">{t(`incident.types.${currentIncident.type}`)}</span>
            </div>
            <div 
              className="rounded-full px-2 py-1 text-xs font-medium text-white" 
              style={{ backgroundColor: getSeverityColor(currentIncident.severity) }}
            >
              {t(`incident.severity.${currentIncident.severity}`)}
            </div>
          </div>

          {/* Verified badge if incident is verified */}
          {currentIncident.still_here_count > currentIncident.no_longer_here_count && (
            <div className="absolute top-24 left-4 z-10 bg-blue-500 px-2 py-1 rounded-full flex items-center">
              <AlertTriangle size={12} className="text-white mr-1" />
              <span className="text-white text-xs font-medium">Verified</span>
            </div>
          )}
        </div>

        {/* Bottom action bar and info */}
        <div className="bg-black text-white">
          {/* User info and timestamp */}
          <div className="flex items-center justify-between px-4 py-3">
            <div className="flex items-center">
              <Avatar className="h-10 w-10 shrink-0 mr-3">
                <AvatarFallback className="bg-pink-900 text-pink-200">
                  {currentIncident.anonymous ? 'AN' : 'LA'}
                </AvatarFallback>
              </Avatar>
              <div>
                <div className="flex items-center">
                  <span className="font-medium">
                    {currentIncident.anonymous ? 'Anonymous' : 'LocalAlert'}
                  </span>
                  {!currentIncident.anonymous && (
                    <span className="ml-1 bg-blue-500 text-white text-xs px-1 rounded">Official</span>
                  )}
                </div>
                <div className="flex items-center text-xs text-gray-400">
                  <Clock size={12} className="mr-1" />
                  <span>{getRelativeTime(currentIncident.created_at)}</span>
                </div>
              </div>
            </div>
            <button className="bg-pink-500 text-white font-medium text-sm px-3 py-1 rounded-full">
              Follow
            </button>
          </div>

          {/* Description */}
          <div className="px-4 pb-3">
            <p className="text-sm leading-tight mb-2 text-white">
              {currentIncident.description || t(`incident.types.${currentIncident.type}`)}
            </p>
            
            {/* Location */}
            <div className="flex items-center text-gray-400 text-xs mb-2">
              <MapPin size={12} className="mr-1" />
              <span>{currentIncident.location_zone}</span>
            </div>
            
            {/* Updates */}
            {hasUpdates && (
              <div className="bg-gray-900 rounded-lg p-3 mb-2">
                <div className="flex items-center mb-2">
                  <RefreshCw size={14} className="text-pink-500 mr-1" />
                  <span className="text-xs font-medium">Latest Updates</span>
                </div>
                {updates.map((update, index) => (
                  <div key={index} className="flex items-start mb-1 last:mb-0">
                    <div className="w-1 h-1 rounded-full bg-gray-500 mt-2 mr-2"></div>
                    <div className="flex-1">
                      <p className="text-xs">{update.content}</p>
                      <p className="text-gray-500 text-xs">{getRelativeTime(update.timestamp)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Action buttons */}
          <div className="flex items-center justify-between px-6 py-2 border-t border-gray-800">
            <button 
              className="flex flex-col items-center" 
              onClick={handleLike}
            >
              <Heart 
                size={26} 
                className={liked ? "text-pink-500" : "text-white"} 
                fill={liked ? "#ec4899" : "none"}
              />
              <span className="text-xs mt-1">{reactions.likes}</span>
            </button>
            
            <button 
              className="flex flex-col items-center"
              onClick={() => setShowComments(!showComments)}
            >
              <MessageSquare size={26} />
              <span className="text-xs mt-1">{reactions.comments}</span>
            </button>
            
            <button 
              className="flex flex-col items-center"
              onClick={handleNativeShare}
            >
              <Share2 size={26} />
              <span className="text-xs mt-1">Share</span>
            </button>
            
            <button 
              className="flex flex-col items-center"
              onClick={handleSave}
            >
              <Bookmark 
                size={26} 
                className={saved ? "text-yellow-500" : "text-white"} 
                fill={saved ? "#eab308" : "none"}
              />
              <span className="text-xs mt-1">{reactions.saves}</span>
            </button>
          </div>
        </div>

        {/* Comment section (slide up from bottom) */}
        {showComments && (
          <div className="absolute inset-0 bg-black bg-opacity-70 z-20 flex flex-col">
            <div className="mt-auto bg-gray-900 rounded-t-2xl p-4 max-h-3/4 overflow-y-auto">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-white font-medium">Comments ({comments.length})</h3>
                <button onClick={() => setShowComments(false)}>
                  <X size={20} className="text-white" />
                </button>
              </div>
              
              {comments.length > 0 ? (
                <div className="space-y-4">
                  {comments.map((comment) => (
                    <div key={comment.id} className="flex items-start">
                      <Avatar className="h-8 w-8 shrink-0 mr-3">
                        <AvatarFallback className="bg-gray-700 text-gray-200">
                          {comment.author?.slice(0, 2) || 'U'}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1">
                        <div className="flex items-center">
                          <span className="text-white font-medium text-sm">{comment.author}</span>
                          {comment.user_id === user?.id && (
                            <span className="ml-1 text-xs text-gray-400">(You)</span>
                          )}
                          <span className="ml-2 text-gray-400 text-xs">{getRelativeTime(comment.created_at)}</span>
                        </div>
                        <p className="text-white text-sm">{comment.content}</p>
                        <div className="flex items-center mt-1 text-xs text-gray-400 space-x-3">
                          <button 
                            className="flex items-center"
                            onClick={() => handleLikeComment(comment.id)}
                          >
                            <Heart size={12} className="mr-1" />
                            <span>{comment.likes || 0}</span>
                          </button>
                          {comment.user_id === user?.id && (
                            <button 
                              onClick={() => {
                                // Handle delete functionality
                                toast({
                                  title: "Delete feature coming soon",
                                  description: "This feature is not yet available",
                                });
                              }}
                            >
                              Delete
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-8 text-gray-400">
                  <MessageCircle size={40} className="mb-2 opacity-50" />
                  <p>No comments yet</p>
                  <p className="text-sm">Be the first to comment on this incident</p>
                </div>
              )}
              
              {/* Comment input */}
              <div className="mt-4 flex items-center">
                <input 
                  type="text" 
                  placeholder={user ? "Add a comment..." : "Sign in to comment"}
                  disabled={!user || isSubmittingComment}
                  value={commentInput}
                  onChange={(e) => setCommentInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleSubmitComment();
                    }
                  }}
                  className="flex-1 bg-gray-800 text-white text-sm rounded-full px-4 py-2 focus:outline-none focus:ring-1 focus:ring-pink-500"
                />
                <button 
                  className={`ml-2 ${user ? 'text-pink-500' : 'text-gray-500'}`}
                  disabled={!user || !commentInput.trim() || isSubmittingComment}
                  onClick={handleSubmitComment}
                >
                  {isSubmittingComment ? (
                    <RefreshCw size={20} className="animate-spin" />
                  ) : (
                    <Send size={20} />
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}